import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { voteAPI } from '../services/api';
import './VotingScreen.css';

// Note: The animal positions are based on the original desktop image map and may need adjustment for the new external image. The current coordinates are placeholders and should be calibrated visually against the actual image used in the app.
// // Animal positions from desktop image map (x, y, radius) - 1440x810
// const ANIMAL_POSITIONS = {
//   'Arara Azul': { x: 750, y: 220, radius: 50 },
//   'Tucano': { x: 910, y: 236, radius: 50 },
//   'Boto Cor-de-Rosa': { x: 1200, y: 230, radius: 50 },
//   'Suçuarana': { x: 400, y: 230, radius: 50 },
//   'Tamanduá-Bandeira': { x: 920, y: 500, radius: 50 },
//   'Teiú': { x: 1300, y: 450, radius: 50 },
//   'Capivara': { x: 170, y: 480, radius: 50 },
//   'Tatu': { x: 850, y: 750, radius: 50 },
//   'Jacaré do Pantanal': { x: 520, y: 740, radius: 50 }
// };

// // Animal positions from mobile image map (x, y, radius) - 1440x2560
// const ANIMAL_POSITIONS_MOBILE = {
//   'Arara Azul': { x: 400, y: 556, radius: 35 },
//   'Tucano': { x: 1070, y: 800, radius: 35 },
//   'Suçuarana': { x: 250, y: 1100, radius: 35 },
//   'Boto Cor-de-Rosa': { x: 1300, y: 1100, radius: 35 },
//   'Tamanduá-Bandeira': { x: 700, y: 1500, radius: 35 },
//   'Capivara': { x: 120, y: 1700, radius: 35 },
//   'Teiú': { x: 1240, y: 1650, radius: 35 },
//   'Jacaré do Pantanal': { x: 200, y: 2300, radius: 35 },
//   'Tatu': { x: 1320, y: 2150, radius: 35 }
// };

// Animal positions from external image map (x, y, radius) - 1360x752 (half-scale)
// Source: html_references/new-imagemap.html
const ANIMAL_POSITIONS_EXTERNAL = {
  'Capivara': { x: 395, y: 557, radius: 35 },
  'Arara Azul': { x: 828, y: 284, radius: 35 },
  'Tucano': { x: 1224, y: 332, radius: 35 },
};
const EXTERNAL_IMAGE_WIDTH = 1360;
const EXTERNAL_IMAGE_HEIGHT = 752;

// Animal positions for external image on mobile - needs manual calibration
// Reference: same 1360x752 image rendered on a narrow viewport
// TODO: open app on mobile and adjust these values visually
const ANIMAL_POSITIONS_EXTERNAL_MOBILE = {
  'Capivara': { x: 430, y: 557, radius: 35 },
  'Arara Azul': { x: 860, y: 300, radius: 35 },
  'Tucano': { x: 1250, y: 400, radius: 35 },
};

function VotingScreen({ campaign, onVoteSubmitted }) {
  const { t, i18n } = useTranslation();
  const [selectedAnimals, setSelectedAnimals] = useState([]);
  const [hoveredAnimal, setHoveredAnimal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const maxChoices = campaign.maxChoices || 3;

  // Track window resize to switch between mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get appropriate positions and dimensions based on device
  const positions = isMobile ? ANIMAL_POSITIONS_EXTERNAL_MOBILE : ANIMAL_POSITIONS_EXTERNAL;
  const imageWidth = EXTERNAL_IMAGE_WIDTH;
  const imageHeight = EXTERNAL_IMAGE_HEIGHT;
  const imageSrc = '/assets/fauna_external.png';

  // Get the campaign title in the current language
  const getCampaignTitle = () => {
    const lang = i18n.language;
    if (campaign.title && campaign.title[lang]) {
      return campaign.title[lang];
    }
    return campaign.name || t('voting.title');
  };

  const handleAnimalClick = (animalName) => {
    if (selectedAnimals.includes(animalName)) {
      // Deselect
      setSelectedAnimals(selectedAnimals.filter(name => name !== animalName));
    } else if (maxChoices === 1) {
      // Single-choice: replace current selection automatically
      setSelectedAnimals([animalName]);
    } else if (selectedAnimals.length < maxChoices) {
      // Multi-choice: select if under limit
      setSelectedAnimals([...selectedAnimals, animalName]);
    }
    setError('');
  };

  const handleVote = async () => {
    if (selectedAnimals.length === 0) return;

    // Validate selection count
    if (selectedAnimals.length !== maxChoices) {
      setError(t('voting.errorCount', { count: maxChoices }));
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await voteAPI.submitVote(selectedAnimals);
      onVoteSubmitted(selectedAnimals);
    } catch (err) {
      setError(err.response?.data?.error || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="voting-screen">
      <div className="voting-container">
        <h1 className="voting-title">{getCampaignTitle()}</h1>
        <p className="voting-subtitle">
          {t('voting.instructions', { count: maxChoices })}
        </p>
        
        <p className="selection-counter">
          {t('voting.selectedCount', { selected: selectedAnimals.length, total: maxChoices, count: maxChoices })}
        </p>

        {selectedAnimals.length > 0 && (
          <div className="selected-animals-list">
            {selectedAnimals.map((animalName, index) => (
              <span key={animalName} className="selected-animal-tag">
                {t(`animals.${animalName}`) || animalName}
              </span>
            ))}
          </div>
        )}

        <div className="image-map-container">
          <img 
            src={imageSrc}
            alt="Brazilian Fauna" 
            className="fauna-image"
            draggable="false"
          />
          
          {Object.entries(positions).map(([animalName, position]) => {
            const isSelected = selectedAnimals.includes(animalName);
            const isHovered = hoveredAnimal === animalName;
            
            return (
              <div
                key={animalName}
                className={`animal-orb ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
                style={{
                  left: `${(position.x / imageWidth) * 100}%`,
                  top: `${(position.y / imageHeight) * 100}%`,
                }}
                onClick={() => handleAnimalClick(animalName)}
                onMouseEnter={() => setHoveredAnimal(animalName)}
                onMouseLeave={() => setHoveredAnimal(null)}
              >
                {isHovered && (
                  <div className="animal-tooltip">
                    {t(`animals.${animalName}`) || animalName}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && <p className="voting-error">{error}</p>}

        <button
          className="vote-button"
          onClick={handleVote}
          disabled={selectedAnimals.length !== maxChoices || loading}
        >
          {loading ? t('loading') : t('voting.submit')}
        </button>
      </div>
    </div>
  );
}

export default VotingScreen;
